import * as React from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  History,
  CheckCircle,
  Clock,
  Activity,
  TrendingUp,
  Target,
  BarChart3,
  Settings,
} from "lucide-react"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

interface CommandPaletteProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const pages = [
  {
    title: "New Scan",
    icon: Search,
    path: "/",
    group: "Scanning",
  },
  {
    title: "Scan History",
    icon: History,
    path: "/history",
    group: "Scanning",
  },
  {
    title: "Review Queue",
    icon: CheckCircle,
    path: "/review-queue",
    group: "Scanning",
  },
  {
    title: "Monitoring",
    icon: Activity,
    path: "/monitoring",
    group: "Automation",
  },
  {
    title: "Scheduled Scans",
    icon: Clock,
    path: "/scheduled-scans",
    group: "Automation",
  },
  {
    title: "Search Visibility",
    icon: TrendingUp,
    path: "/visibility-trends",
    group: "Analytics",
  },
  {
    title: "Repeat Offenders",
    icon: Target,
    path: "/domain-analytics",
    group: "Analytics",
  },
  {
    title: "API Monitor",
    icon: BarChart3,
    path: "/api-monitor",
    group: "System",
  },
  {
    title: "Settings",
    icon: Settings,
    path: "/settings",
    group: "System",
  },
]

export function CommandPalette({ open, setOpen }: CommandPaletteProps) {
  const navigate = useNavigate()

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(!open)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [open, setOpen])

  const handleSelect = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  const groups = pages.reduce((acc, page) => {
    if (!acc[page.group]) {
      acc[page.group] = []
    }
    acc[page.group].push(page)
    return acc
  }, {} as Record<string, typeof pages>)

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {Object.entries(groups).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((page) => (
              <CommandItem
                key={page.path}
                onSelect={() => handleSelect(page.path)}
              >
                <page.icon className="mr-2 h-4 w-4" />
                <span>{page.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
